import { useEffect } from 'react';

// Your Agents shares one selection between the genome and the card roster: picking an agent
// dims the figure and brings its card forward. A press anywhere OFF the selection controls
// returns to rest — the Maestro at the front, the genome undimmed — the web port of native's
// tap-the-background-to-deselect (`AgentConstellation` on both phones), widened to the whole
// screen. The controls opt out of the dismissal by carrying `data-agent-control`; a press that
// resolves to none of them clears the selection.

export const AGENT_CONTROL_ATTR = 'data-agent-control';
const AGENT_CONTROL_SELECTOR = `[${AGENT_CONTROL_ATTR}]`;

/** Spread onto a genome node / agent card so a press on it does NOT clear the selection. */
export const agentControlProps = { [AGENT_CONTROL_ATTR]: '' } as const;

interface HasClosest {
  closest(selector: string): unknown;
}

function hasClosest(target: unknown): target is HasClosest {
  return typeof (target as HasClosest | null | undefined)?.closest === 'function';
}

/**
 * Whether a pointer press should clear the current selection: a selection is active AND the
 * press landed outside every element marked `data-agent-control`. A target with no `closest`
 * (a non-element, or none) counts as outside.
 */
export function pressClearsSelection(active: boolean, target: unknown): boolean {
  if (!active) return false;
  if (!hasClosest(target)) return true;
  return target.closest(AGENT_CONTROL_SELECTOR) == null;
}

/** While `active`, clear the selection on a pointer press outside the selection controls. */
export function useDeselectOnOutsidePress(active: boolean, deselect: () => void): void {
  useEffect(() => {
    if (!active) return;
    const handlePress = (event: PointerEvent) => {
      if (pressClearsSelection(true, event.target)) deselect();
    };
    document.addEventListener('pointerdown', handlePress);
    return () => document.removeEventListener('pointerdown', handlePress);
  }, [active, deselect]);
}
